<template>
  <div
    aria-label="Create run sheet"
    role="main"
  >
    <div
      v-if="isLoading === true"
      class="text-center mb-2"
    >
      <b-spinner
        variant="primary"
        label="Text Centered"
      />
    </div>
    <b-form-group
      v-if="user.role === 'admin' || user.role === 'superadmin'"
      label="Dépôt"
      label-for="select-repository"
      aria-label="Sélection du dépôt"
    >
      <validation-provider
        #default="{ errors }"
        name="Dépôt"
      >
        <v-select
          v-model="repository"
          :clearable="false"
          placeholder="Sélectionner un dépôt"
          label="name"
          :options="repositories"
          input-id="select-repository"
          aria-label="Liste des dépôts disponibles"
          @input="getDepositOrder"
        />
        <small class="text-danger">{{ errors[0] }}</small>
      </validation-provider>
    </b-form-group>
    <b-form-group
      label="Référence du colis à ajouter à la run sheet"
      label-for="input-colis-ref"
      aria-label="Champ de saisie de la référence du colis"
    >
      <b-input-group class="input-group-merge roadmap-scan-input-group">
        <b-input-group-prepend is-text>
          <feather-icon icon="MaximizeIcon" />
        </b-input-group-prepend>
        <b-form-input
          id="input-colis-ref"
          ref="select"
          v-model="ref"
          placeholder="Scanner ou saisir la référence du colis"
          aria-label="Entrer la référence du colis à ajouter"
          @change="checkOrder"
        />
        <b-input-group-append>
          <b-button
            :variant="isPhoneScanning ? 'danger' : 'outline-primary'"
            :disabled="isStartingCamera || !canUseCameraScanner"
            class="phone-scanner-button"
            @click="togglePhoneScanner"
          >
            <feather-icon
              :icon="isPhoneScanning ? 'XIcon' : 'CameraIcon'"
              class="mr-1"
            />
            {{ isPhoneScanning ? 'Arrêter' : 'Scanner téléphone' }}
          </b-button>
        </b-input-group-append>
      </b-input-group>
    </b-form-group>
    <b-alert
      v-if="!canUseCameraScanner"
      variant="warning"
      show
      class="mb-2"
    >
      Le scan caméra nécessite HTTPS ou localhost sur le téléphone. Utilisez une URL sécurisée ou le champ référence.
    </b-alert>
    <b-card
      v-if="isPhoneScanning || isStartingCamera"
      class="mb-2 phone-scanner-card"
      body-class="p-1"
    >
      <div
        v-if="isStartingCamera"
        class="d-flex align-items-center justify-content-center scanner-loading"
      >
        <b-spinner
          small
          variant="primary"
          class="mr-1"
        />
        Ouverture caméra...
      </div>
      <div
        v-show="isPhoneScanning"
        class="phone-scanner-frame"
      >
        <video
          ref="phoneScannerVideo"
          class="phone-scanner-video"
          autoplay
          muted
          playsinline
        />
        <div class="phone-scanner-target" />
      </div>
    </b-card>
    <!-- table -->
    <div class="d-flex justify-content-between">
      <div
        v-if="isLoading === true"
        class="text-center mb-2"
      >
        <b-spinner
          variant="primary"
          label="Text Centered"
        />
      </div>
      <b-card>
        <h4 aria-live="polite">
          Colis au dépôt : {{ orders.length }}
        </h4>
        <vue-good-table
          :columns="columns"
          :rows="orders"
          :search-options="{
            enabled: true,
            externalQuery: searchTerm ,
          }"
        >
          <template
            slot="table-row"
            slot-scope="props"
          >
            <span v-if="props.column.field === 'id'">
              <span> {{ props.row.display_cross_tenant_barcode && props.row.cross_tenant_barcode ? props.row.cross_tenant_barcode : props.row.id }}</span><br>
              <span
                v-if="props.row.is_an_exchange"
                style="color:red ; font-weight: bold;"
              > Echange </span>
              <b-badge
                v-if="props.row.status === 18"
                variant="info"
                class="ml-1"
              >Rendez Vous</b-badge>
              <span v-if="props.row.status === 18 && props.row.planned_data">
                <br><small>Prévu: {{ props.row.planned_data }}</small>
              </span>
              <!-- Last Return Reason: small with icon -->
              <div
                v-if="props.row.last_return_reason"
                class="text-wrap mt-1 d-flex align-items-center"
                style="max-width: 200px; font-size: 11px; color: #888;"
              >
                <feather-icon
                  icon="AlertCircleIcon"
                  size="12"
                  class="mr-1"
                  style="color:#e74c3c;"
                />
                <span>
                  {{ props.row.last_return_reason.return_reason }}
                  <span class="text-muted ml-1">({{ formatDate(props.row.last_return_reason.created_at) }})</span>
                  <span
                    v-if="props.row.last_return_reason.scheduled_date"
                    class="text-info ml-1"
                  >
                    Prévu: {{ formatDate(props.row.last_return_reason.scheduled_date) }}
                  </span>
                  <span
                    v-if="props.row.last_return_reason.additional_note"
                    class="text-secondary ml-1"
                  >
                    Note: {{ props.row.last_return_reason.additional_note }}
                  </span>
                </span>
              </div>
            </span>
            <!-- Column: Action -->
            <span v-else-if="props.column.field === 'action'">
              <span>
                <feather-icon
                  :id="`invoice-row-${props.row.id}-show-icon`"
                  icon="CheckSquareIcon"
                  class="cursor-pointer mr-1"
                  size="16"
                  color="green"
                  @click="addItem(props.row)"
                />
                <b-tooltip
                  title="Ajouter"
                  class="cursor-pointer"
                  :target="`invoice-row-${props.row.id}-show-icon`"
                />

              </span>
            </span>

            <!-- Column: Common -->
            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>
          <!-- pagination -->
          <template
            slot="pagination-bottom"
            slot-scope="props"
          >
            <div class="d-flex justify-content-between flex-wrap">
              <div class="d-flex align-items-center mb-0 mt-1">
                <span class="text-nowrap">
                  Affichage 1 à
                </span>
                <b-form-select
                  v-model="pageLength"
                  :options="['50','100','500']"
                  class="mx-1"
                  @input="(value)=>props.perPageChanged({currentPerPage:value})"
                />
                <span class="text-nowrap "> de {{ props.total }} résultats trouvés </span>
              </div>
              <div>
                <b-pagination
                  :value="1"
                  :total-rows="props.total"
                  :per-page="pageLength"
                  first-number
                  last-number
                  align="right"
                  prev-class="prev-item"
                  next-class="next-item"
                  class="mt-1 mb-0"
                  @input="(value)=>props.pageChanged({currentPage:value})"
                >
                  <template #prev-text>
                    <feather-icon
                      icon="ChevronLeftIcon"
                      size="18"
                    />
                  </template>
                  <template #next-text>
                    <feather-icon
                      icon="ChevronRightIcon"
                      size="18"
                    />
                  </template>
                </b-pagination>
              </div>
            </div>
          </template>
        </vue-good-table>
      </b-card>
      <b-card class="ml-1">
        <h4 aria-live="polite">
          Colis dans la run sheet : {{ selected.length }}
        </h4>
        <vue-good-table
          :columns="columns"
          :rows="selected"
          :search-options="{
            enabled: true,
            externalQuery: searchTerm }"
        >
          <template
            slot="table-row"
            slot-scope="props"
          >
            <span v-if="props.column.field === 'id'">
              <span> {{ props.row.display_cross_tenant_barcode && props.row.cross_tenant_barcode ? props.row.cross_tenant_barcode : props.row.id }}</span><br>
              <span
                v-if="props.row.is_an_exchange"
                style="color:red ; font-weight: bold;"
              > Echange </span>
              <b-badge
                v-if="props.row.status === 18"
                variant="info"
                class="ml-1"
              >Rendez Vous</b-badge>
            </span>
            <!-- Column: Action -->
            <span v-else-if="props.column.field === 'action'">
              <span>
                <feather-icon
                  :id="`invoice-row-${props.row.id}-show-icon`"
                  icon="DeleteIcon"
                  color="red"
                  class="cursor-pointer mr-1"
                  size="16"
                  @click="resetItem(props.row)"
                />
                <b-tooltip
                  title="Annuler"
                  class="cursor-pointer"
                  :target="`invoice-row-${props.row.id}-show-icon`"
                />

              </span>
            </span>

            <!-- Column: Common -->

            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>

          <!-- pagination -->
          <template
            slot="pagination-bottom"
            slot-scope="props"
          >
            <div class="d-flex justify-content-between flex-wrap">
              <div class="d-flex align-items-center mb-0 mt-1">
                <span class="text-nowrap">
                  Affichage 1 à
                </span>
                <b-form-select
                  v-model="pageLength"
                  :options="['50','100','500']"
                  class="mx-1"
                  @input="(value)=>props.perPageChanged({currentPerPage:value})"
                />
                <span class="text-nowrap "> de {{ props.total }} résultats trouvés </span>
              </div>
              <div>
                <b-pagination
                  :value="1"
                  :total-rows="props.total"
                  :per-page="pageLength"
                  first-number
                  last-number
                  align="right"
                  prev-class="prev-item"
                  next-class="next-item"
                  class="mt-1 mb-0"
                  @input="(value)=>props.pageChanged({currentPage:value})"
                >
                  <template #prev-text>
                    <feather-icon
                      icon="ChevronLeftIcon"
                      size="18"
                    />
                  </template>
                  <template #next-text>
                    <feather-icon
                      icon="ChevronRightIcon"
                      size="18"
                    />
                  </template>
                </b-pagination>
              </div>
            </div>
          </template>

        </vue-good-table>
        <b-form-group
          v-if="drivers.length > 0"
          label="Sélectionner le livreur"
          label-for="select-livreur"
          aria-label="Sélection du livreur pour la run sheet"
        >
          <validation-provider
            #default="{ errors }"
            name="Livreur"
          >
            <v-select
              v-model="livreur"
              :clearable="false"
              placeholder="Livreur"
              label="username"
              :options="drivers"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>
        <b-form-group
          label="Sélectionner la voiture"
          label-for="select-voiture"
          aria-label="Sélection de la voiture pour la run sheet"
        >
          <validation-provider
            #default="{ errors }"
            name="Voiture"
          >
            <v-select
              v-model="livreur.car"
              :clearable="false"
              placeholder="Voiture"
              label="serial_number"
              :options="cars"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>
        <b-form-group class="d-flex justify-content-end">
          <span id="save-roadmap-btn-wrapper">
            <b-button
              ref="saveButton"
              variant="gradient-primary"
              class="ml-1 d-inline-block save-btn"
              :class="{ 'not-allowed': isLoading || !isRoadmapValid }"
              :disabled="isLoading || !isRoadmapValid"
              aria-label="Créer la run sheet avec les colis sélectionnés"
              @click="addRoadMap"
            >
              <feather-icon icon="PlusIcon" />
              <span class="ml-1">Enregistrer</span>
            </b-button>
            <b-tooltip
              v-if="isLoading || !isRoadmapValid"
              target="save-roadmap-btn-wrapper"
              triggers="hover focus"
              placement="top"
            >
              <template v-if="isLoading">
                Veuillez patienter, chargement en cours...
              </template>
              <template v-else>
                <span v-if="selected.length === 0">Veuillez sélectionner au moins un colis.</span>
                <span v-else-if="!livreur || !livreur.id">Veuillez sélectionner un livreur.</span>
                <span v-else>Veuillez remplir tous les champs obligatoires.</span>
              </template>
            </b-tooltip>
          </span>
        </b-form-group>
      </b-card>
    </div>
    <div
      class="sr-only"
      aria-live="polite"
    >
      Veuillez remplir tous les champs obligatoires pour créer une run sheet. Les champs marqués d'un astérisque sont obligatoires.
    </div>
  </div>
</template>

<script>
import Ripple from 'vue-ripple-directive'
import axios from 'axios'
import { orderMatchesReference } from '@/utils/orderReference'
import vSelect from 'vue-select'
import storeAuth from '@/store/store'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import jsQR from 'jsqr'
import {
  BCard, BButton, BPagination, BFormGroup, BFormInput, BInputGroup, BInputGroupPrepend, BInputGroupAppend, BFormSelect, BTooltip, BSpinner, BAlert,

} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import { ValidationProvider } from 'vee-validate'
import zeroTentative from '@/assets/audio/0tentative.mp3'
import oneTentative from '@/assets/audio/1tentative.mp3'
import twoTentative from '@/assets/audio/2tentative.mp3'
import threeTentative from '@/assets/audio/3tentative.mp3'
import fourTentative from '@/assets/audio/4tentative.mp3'
import fiveTentative from '@/assets/audio/5tentative.mp3'
import isAnExchange from '@/assets/audio/exchange.mp3'
import ok from '@/assets/audio/ok.mp3'
import errorVoice from '@/assets/audio/error.mp3'
import alreadyScanned from '@/assets/audio/colis-deja-scanne.mp3'
import rendezVous from '@/assets/audio/rendez-vous.mp3'

export default {
  name: 'CreateRoadmap',
  components: {
    BButton,
    ValidationProvider,
    VueGoodTable,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BInputGroupAppend,
    BFormSelect,
    BPagination,
    BTooltip,
    vSelect,
    BCard,
    BSpinner,
    BAlert,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      isLoading: false,
      drivers: [],
      livreur: {},
      date: '01-10-2021',
      status: '',
      pageLength: 100,
      dir: false,
      columns: [
        {
          label: 'Action',
          field: 'action',
        },
        {
          label: 'BL',
          field: 'id',
        },
        {
          label: 'Client',
          field: 'name',
          filterOptions: {
            enabled: true,
            placeholder: 'chercher client',
          },
        },
        {
          label: 'Fournisseur',
          field: 'supplier.username',
          filterOptions: {
            enabled: true,
            placeholder: 'Fournisseur',
          },
        },
        {
          label: 'Gouvernorat',
          field: 'governorate',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher gouvernorat',
          },
        },
        {
          label: 'Délégation',
          field: 'delegation',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher délégation',
          },
        },
        {
          label: 'Adresse',
          field: 'address',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher adresse',
          },
        },
        {
          label: 'tel',
          field: 'phone1',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher tel',
          },
        },
      ],
      role: '',
      orders: [],
      selected: [],
      searchTerm: '',
      ref: '',
      user: {},
      cars: [],
      car: {},
      repository: {},
      repositories: [],
      zeroTentative,
      oneTentative,
      twoTentative,
      threeTentative,
      fourTentative,
      fiveTentative,
      isAnExchange,
      ok,
      errorVoice,
      alreadyScanned,
      rendezVous,
      desynchronizingOrderId: null,
      canUseCameraScanner: typeof navigator !== 'undefined' && !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia),
      isPhoneScanning: false,
      isStartingCamera: false,
      isProcessingPhoneScan: false,
      phoneScannerStream: null,
      phoneScannerFrameId: null,
      phoneScannerCanvas: null,
      phoneScannerCanvasContext: null,
      phoneScannerDetector: null,
      lastScannedRef: '',
      lastScannedAt: 0,
    }
  },
  computed: {
    isRoadmapValid() {
      return (
        this.livreur
        && this.livreur.id
        && this.selected.length > 0
      )
    },
    requireOrderDesynchronization() {
      return Boolean(
        storeAuth.state.company
        && storeAuth.state.company.require_order_desynchronization_before_roadmap,
      )
    },
  },
  async created() {
    await storeAuth.dispatch('fetchCompanyInfo')
    this.user = storeAuth.state.user
    if (this.user.role === 'manager') {
      this.getDepositOrder()
    }
    this.getRepositories()
    this.getDeliveryMans()
    this.getCars()
  },
  mounted() {
    this.$refs.select.focus()
  },
  beforeDestroy() {
    this.stopPhoneScanner()
  },
  methods: {
    async togglePhoneScanner() {
      if (this.isPhoneScanning || this.isStartingCamera) {
        this.stopPhoneScanner()
      } else {
        await this.startPhoneScanner()
      }
    },
    async startPhoneScanner() {
      if (!this.canUseCameraScanner) return
      this.isStartingCamera = true
      try {
        this.phoneScannerDetector = this.createBarcodeDetector()
        this.phoneScannerCanvas = document.createElement('canvas')
        this.phoneScannerCanvasContext = this.phoneScannerCanvas.getContext('2d', { willReadFrequently: true })
        this.phoneScannerStream = await this.openCameraStream()
        this.isPhoneScanning = true
        await this.$nextTick()
        const video = this.$refs.phoneScannerVideo
        video.srcObject = this.phoneScannerStream
        await video.play()
        this.scanPhoneCameraFrame()
      } catch (error) {
        this.showToast('danger', 'top-center', this.getCameraErrorMessage(error))
        this.stopPhoneScanner()
      } finally {
        this.isStartingCamera = false
      }
    },
    async openCameraStream() {
      try {
        return await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
          },
          audio: false,
        })
      } catch (error) {
        if (error && ['OverconstrainedError', 'ConstraintNotSatisfiedError'].includes(error.name)) {
          return navigator.mediaDevices.getUserMedia({
            video: true,
            audio: false,
          })
        }
        throw error
      }
    },
    getCameraErrorMessage(error) {
      const errorName = error && error.name
      if (errorName === 'NotAllowedError' || errorName === 'PermissionDeniedError') {
        return 'Accès caméra refusé. Autorisez la caméra dans le navigateur puis réessayez.'
      }
      if (errorName === 'NotFoundError' || errorName === 'DevicesNotFoundError') {
        return 'Aucune caméra détectée sur cet appareil.'
      }
      if (errorName === 'NotReadableError' || errorName === 'TrackStartError') {
        return 'La caméra est déjà utilisée par une autre application ou indisponible.'
      }
      if (errorName === 'SecurityError' || (typeof window !== 'undefined' && !window.isSecureContext)) {
        return 'La caméra nécessite HTTPS ou localhost sur le même appareil.'
      }
      return 'Impossible d’ouvrir la caméra pour scanner le code.'
    },
    createBarcodeDetector() {
      if (typeof window === 'undefined' || !('BarcodeDetector' in window)) return null
      try {
        return new window.BarcodeDetector({
          formats: ['qr_code', 'code_128', 'code_39', 'ean_13', 'ean_8', 'upc_a', 'upc_e'],
        })
      } catch (error) {
        try {
          return new window.BarcodeDetector({ formats: ['qr_code'] })
        } catch (fallbackError) {
          return null
        }
      }
    },
    stopPhoneScanner() {
      if (this.phoneScannerFrameId) {
        window.cancelAnimationFrame(this.phoneScannerFrameId)
        this.phoneScannerFrameId = null
      }
      if (this.phoneScannerStream) {
        this.phoneScannerStream.getTracks().forEach(track => track.stop())
        this.phoneScannerStream = null
      }
      this.phoneScannerCanvas = null
      this.phoneScannerCanvasContext = null
      this.phoneScannerDetector = null
      this.isPhoneScanning = false
    },
    async scanPhoneCameraFrame() {
      if (!this.isPhoneScanning || !this.$refs.phoneScannerVideo) return
      try {
        const codes = this.phoneScannerDetector
          ? await this.phoneScannerDetector.detect(this.$refs.phoneScannerVideo)
          : []
        if (codes.length && codes[0].rawValue) {
          await this.handlePhoneScan(codes[0].rawValue)
        } else {
          const decodedValue = this.decodeQrFromVideoFrame()
          if (decodedValue) {
            await this.handlePhoneScan(decodedValue)
          }
        }
      } catch (error) {
        this.showToast('danger', 'top-center', 'Erreur pendant la lecture du code')
        this.stopPhoneScanner()
        return
      }
      this.phoneScannerFrameId = window.requestAnimationFrame(this.scanPhoneCameraFrame)
    },
    decodeQrFromVideoFrame() {
      const video = this.$refs.phoneScannerVideo
      if (!video || !this.phoneScannerCanvas || !this.phoneScannerCanvasContext) return ''
      if (!video.videoWidth || !video.videoHeight) return ''

      this.phoneScannerCanvas.width = video.videoWidth
      this.phoneScannerCanvas.height = video.videoHeight
      this.phoneScannerCanvasContext.drawImage(video, 0, 0, video.videoWidth, video.videoHeight)
      const imageData = this.phoneScannerCanvasContext.getImageData(0, 0, video.videoWidth, video.videoHeight)
      const code = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: 'dontInvert',
      })

      return code && code.data ? code.data : ''
    },
    async handlePhoneScan(rawValue) {
      const scannedRef = this.extractOrderReference(rawValue)
      if (!scannedRef || this.isProcessingPhoneScan) return

      const now = Date.now()
      if (this.lastScannedRef === scannedRef && now - this.lastScannedAt < 2000) {
        this.lastScannedAt = now
        return
      }
      this.lastScannedRef = scannedRef
      this.lastScannedAt = now

      this.isProcessingPhoneScan = true
      try {
        this.ref = scannedRef
        await this.checkOrder()
      } finally {
        this.isProcessingPhoneScan = false
      }
    },
    extractOrderReference(rawValue) {
      const value = rawValue ? rawValue.toString().trim() : ''
      if (!value) return ''

      try {
        const url = new URL(value)
        const queryRef = url.searchParams.get('order_id')
          || url.searchParams.get('order')
          || url.searchParams.get('id')
          || url.searchParams.get('ref')
          || url.searchParams.get('barcode')
        if (queryRef) return queryRef.trim()

        const pathParts = url.pathname.split('/').filter(Boolean)
        if (pathParts.length) return pathParts[pathParts.length - 1].trim()
      } catch (error) {
        // Plain BL IDs and cross-tenant barcodes are expected on printed labels.
      }

      return value
    },
    async checkOrder() {
      const existingOrder = this.selected.find(item => orderMatchesReference(item, this.ref))

      if (existingOrder) {
        await this.withTitle('Colis déjà scanné sur la liste sélectionnée')
        this.playAudio(this.alreadyScanned)
      } else {
        const searchedItem = this.orders.find(element => orderMatchesReference(element, this.ref))

        if (searchedItem) {
          // Play rendez-vous audio if status is 18
          if (searchedItem.status === 18) {
            await this.playAudio(this.rendezVous)
            await this.delay(500)
          }
          if (searchedItem.is_an_exchange) {
            await this.playAudio(this.isAnExchange)
            await this.delay(500)
          }
          const added = await this.addItem(searchedItem)
          this.ref = ''
          if (!added) return
          if (searchedItem.return_number === 0) {
            this.playAudio(this.zeroTentative)
          } else if (searchedItem.return_number === 1) {
            this.playAudio(this.oneTentative)
          } else if (searchedItem.return_number === 2) {
            this.playAudio(this.twoTentative)
          } else if (searchedItem.return_number === 3) {
            this.playAudio(this.threeTentative)
          } else if (searchedItem.return_number === 4) {
            this.playAudio(this.fourTentative)
          } else if (searchedItem.return_number === 5) {
            this.playAudio(this.fiveTentative)
          }
        } else {
          this.playAudio(this.errorVoice)
          this.ref = ''
        }
      }
    },
    async playAudio(payload) {
      const voice = new Audio(payload)
      voice.addEventListener('canplaythrough', () => {
        voice.play()
      })
    },
    async delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    },
    async addItem(item) {
      if (this.requiresDesynchronization(item)) {
        const desynchronized = await this.confirmDesynchronization(item)
        if (!desynchronized) {
          this.ref = ''
          this.focusScanner()
          return false
        }
      }

      this.selected.push(item)
      const i = this.orders.findIndex(element => element.id === item.id)
      if (i !== -1) this.orders.splice(i, 1)
      this.focusScanner()
      return true
    },
    async resetItem(item) {
      this.orders.push(item)
      const i = this.selected.findIndex(element => element.id === item.id)
      this.selected.splice(i, 1)
    },
    async getRepositories() {
      const { data } = await axios.get('/api/repositories/')
      this.repositories = data
    },
    async getCars() {
      try {
        const { data } = await axios.get('/api/cars/dropdown/')
        this.cars = data
      } catch (error) {
        this.cars = []
      }
    },
    async withTitle(text) {
      await this.$swal({
        title: 'Alerte',
        icon: 'warning',
        text,
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      })
      this.ref = ''
    },
    requiresDesynchronization(item) {
      if (item && Object.prototype.hasOwnProperty.call(
        item,
        'requires_desynchronization_before_roadmap',
      )) {
        return Boolean(item.requires_desynchronization_before_roadmap)
      }

      return Boolean(
        this.requireOrderDesynchronization
        && item
        && item.is_synchronized_with_outsourcing_company,
      )
    },
    outsourcingCompanyName(item) {
      if (!item || !item.outsourcing_company) return 'la société de livraison externe'
      return item.outsourcing_company.name || item.outsourcing_company.display_name || 'la société de livraison externe'
    },
    async confirmDesynchronization(item) {
      if (this.desynchronizingOrderId) return false

      const providerName = this.outsourcingCompanyName(item)
      const externalReference = item.external_id || '-'
      const confirmation = await this.$swal({
        title: 'Colis déjà synchronisé',
        text: `Ce colis est synchronisé avec ${providerName} (référence externe : ${externalReference}). Il ne peut pas être ajouté à une run sheet tant qu'il reste synchronisé. Désynchronisez-le d'abord afin d'empêcher le prestataire de modifier son statut pendant la suite du traitement.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Désynchroniser et ajouter',
        cancelButtonText: 'Annuler',
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      })

      if (!confirmation.isConfirmed) return false

      this.desynchronizingOrderId = item.id
      try {
        const encodedOrderId = encodeURIComponent(item.id)
        await axios.post(`/api/roadmaps/desynchronize-order/${encodedOrderId}/`)
        Object.assign(item, {
          external_id: null,
          outsourcing_company: null,
          is_synchronized_with_outsourcing_company: false,
          requires_desynchronization_before_roadmap: false,
        })
        this.showToast('success', 'top-center', 'Colis désynchronisé et prêt à être ajouté à la run sheet')
        return true
      } catch (error) {
        const message = error.response?.data?.detail
          || error.response?.data?.error
          || 'Erreur lors de la désynchronisation du colis'
        this.showToast('danger', 'top-center', message)
        return false
      } finally {
        this.desynchronizingOrderId = null
        this.focusScanner()
      }
    },
    focusScanner() {
      this.$nextTick(() => {
        if (this.$refs.select) this.$refs.select.focus()
      })
    },
    async addRoadMap() {
      // Prepare summary for confirmation dialog
      const summary = `Résumé de la création :\n\n- Livreur : ${this.livreur?.username || ''}\n- Véhicule : ${this.livreur?.car?.serial_number || ''}\n- Nombre de colis : ${this.selected.length}`
      const confirmed = await this.$swal({
        title: 'Confirmer la création',
        text: summary,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Oui, créer',
        cancelButtonText: 'Annuler',
        customClass: {
          confirmButton: 'btn btn-success',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      })
      if (!confirmed.isConfirmed) return

      this.isLoading = true
      const covertedOrders = this.selected.map(order => order.id)

      const obj = {
        orders: covertedOrders,
        delivery_man_id: this.livreur.id,
      }

      // Include car_id if a car is selected
      if (this.livreur.car && this.livreur.car.id) {
        obj.car_id = this.livreur.car.id
      }

      // Only include repository_id for admin and superadmin users
      if (storeAuth.state.user.role === 'admin' || storeAuth.state.user.role === 'superadmin') {
        if (!this.repository || !this.repository.id) {
          this.showToast('danger', 'top-center', 'Veuillez sélectionner un dépôt')
          this.isLoading = false
          return
        }
        obj.repository_id = this.repository.id
      }

      try {
        const { data } = await axios.post('/api/roadmaps/create-roadmap/', obj)
        this.showToast('success', 'top-center', 'Run sheet ajouté avec succés')
        this.$router.push(`/roadmaps/show/${data}`)
      } catch (error) {
        this.showToast('danger', 'top-center', error.response?.data?.detail || error.toString())
      } finally {
        this.isLoading = false
      }
    },
    formatDate(date) {
      if (!date) {
        return null
      }
      const [year, month, day] = date.split('-')
      const day1 = day.substr(0, 2)
      return `${day1.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`
    },
    showToast(variant, position, text) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: 'Notification',
            icon: 'InfoIcon',
            text,
            variant,
          },
        },
        {
          position,
        },
      )
    },
    async getDeliveryMans() {
      this.isLoading = true
      try {
        const { data } = await axios.get('api/auth/users/drivers-for-roadmap/', {
          params: { repository_id: this.repository?.id || this.user.repository },
        })
        this.drivers = data
      } catch (error) {
        this.drivers = []
      }
      this.isLoading = false
    },
    async getDepositOrder() {
      this.isLoading = true
      this.livreur = {}
      await this.getDeliveryMans()
      this.selected = []
      let repository = null
      if (storeAuth.state.user.role === 'admin' || storeAuth.state.user.role === 'superadmin') {
        repository = this.repository.id
      } else {
        repository = storeAuth.state.user.repository
      }
      try {
        const { data } = await axios.get(`/api/orders/orders-allowed-for-roadmap/${repository}`)
        this.orders = data
      } catch (error) {
        // handle error if needed
      }
      this.isLoading = false
    },
    formatFn(date) {
      if (!date) {
        return null
      }
      const [year, month, day] = date.split('-')
      const day1 = day.substr(0, 2)
      const h = date.slice(11, 19)
      return `${year}-${month.padStart(2, '0')}-${day1.padStart(2, '0')} | ${h}`
    },

  },
}
</script>

<style lang="scss" >
@import "@core/scss/vue/libs/vue-select.scss";
@import '@core/scss/vue/libs/vue-good-table.scss';

// Accessibilité : focus visible pour les champs interactifs
input:focus, select:focus, .v-select:focus {
  outline: 2px solid #005fcc !important;
  outline-offset: 2px;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0,0,0,0);
  border: 0;
}

.save-btn.not-allowed,
.save-btn[disabled] {
  cursor: not-allowed !important;
  opacity: 0.6 !important;
  pointer-events: auto !important;
}

.roadmap-scan-input-group {
  width: 100%;
}

.phone-scanner-button {
  white-space: nowrap;
}

.phone-scanner-card {
  width: 100%;
  max-width: 520px;
}

.scanner-loading {
  min-height: 220px;
}

.phone-scanner-frame {
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: 520px;
  aspect-ratio: 4 / 3;
  background: #111;
  border-radius: 6px;
}

.phone-scanner-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.phone-scanner-target {
  position: absolute;
  top: 18%;
  right: 18%;
  bottom: 18%;
  left: 18%;
  border: 2px solid rgba(255, 255, 255, 0.9);
  border-radius: 6px;
  box-shadow: 0 0 0 999px rgba(0, 0, 0, 0.22);
  pointer-events: none;
}

@media (max-width: 575.98px) {
  .roadmap-scan-input-group {
    align-items: stretch;
    flex-wrap: wrap;
  }

  .roadmap-scan-input-group > .input-group-prepend {
    flex: 0 0 auto;
  }

  .roadmap-scan-input-group > .form-control {
    min-width: 0;
  }

  .roadmap-scan-input-group > .input-group-append {
    flex: 0 0 100%;
    margin-top: 0.5rem;
    margin-left: 0;
  }

  .roadmap-scan-input-group > .input-group-append .btn,
  .phone-scanner-button {
    width: 100%;
    border-radius: 0.357rem;
  }

  .phone-scanner-card {
    max-width: none;
  }

  .phone-scanner-frame {
    max-width: none;
    aspect-ratio: 3 / 4;
  }
}

</style>
